import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import { FaPlus, FaPen, FaBan, FaCheck, FaExclamationTriangle } from "react-icons/fa";

import api from "services/api";
import { useAuth } from "hooks/auth";
import { useToast } from "hooks/toast";
import useDebounce from "hooks/useDebounce";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import { toggleSort, sortRows, paginate } from "utils/tableHelpers";
import { exportCsv } from "utils/exportCsv";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import ShellCard, { TableSurface } from "components/ShellCard";
import Status from "components/Status";

import "./TiposVeiculo.css";

// Tipo de Veículo (PO1-3103): catálogo GLOBAL. Não depende da empresa
// selecionada no topo, por isso fica no Everlog Admin → Catálogos. Cada tipo é
// do cadastro base (todos os clientes) ou exclusivo de um cliente (PO1-3489):
// o exclusivo só aparece nos combos daquele cliente. Só o admin Everlog cria, edita, ativa e inativa: o
// backend devolve 403 para os demais e aqui os botões somem se a tela for aberta
// por quem não é admin. Sem exclusão: inativar tira o tipo do combo de novas
// tabelas, e as existentes seguem valendo.
// Molde: CadastrosGerais/Negocios (padrão do épico PO1-2723).

const URL_BASE = "/cadastros/tipos-veiculo";

const DEFAULT_SORT = { field: "nome", dir: "asc" };

// Limites das colunas da tabela TipoVeiculo (iguais aos do backend).
export const LIMITES = { codigo: 15, nome: 100, descricao: 300, eixos: 20, capacidade: 99999999.99 };

function createEmptyForm() {
  return {
    codigo: "",
    nome: "",
    descricao: "",
    qtdEixos: "",
    capacidade: "",
    status: "active",
    idEmbarcador: "",
  };
}

// Filtro "Cliente": "" = todos, "base" = cadastro base, senão o Id do cliente.
export function passaFiltroCliente(item, filtro) {
  if (!filtro) return true;
  if (filtro === "base") return !item.idEmbarcador;
  return String(item.idEmbarcador || "") === String(filtro);
}

export function rotuloCliente(item) {
  return item && item.idEmbarcador ? item.embarcador || `Cliente ${item.idEmbarcador}` : "Base";
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function isActive(status) {
  if (typeof status === "boolean") return status;
  const normalized = normalizeText(status);
  return !["0", "false", "inativo", "nao"].includes(normalized);
}

// "1.700,50" ou "1700.5" -> 1700.5; vazio -> null; inválido -> NaN.
export function parseCapacidade(value) {
  const s0 = String(value === null || value === undefined ? "" : value).trim();
  if (!s0) return null;
  const s = s0.includes(",") ? s0.replace(/\./g, "").replace(",", ".") : s0;
  return /^\d+(\.\d+)?$/.test(s) ? Number(s) : NaN;
}

// 27000 -> "27.000"; 1700.5 -> "1.700,5". Sem toLocaleString: o build roda em Node 12
// (small-icu), que formataria em inglês.
export function formatKg(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const [inteiro, decimal] = (Math.round(n * 100) / 100).toString().split(".");
  const milhar = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decimal ? `${milhar},${decimal}` : milhar;
}

function capacidadeParaForm(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value).replace(".", ",");
}

export function validarForm(form) {
  const errors = {};
  const codigo = form.codigo.trim();
  const nome = form.nome.trim();
  if (!codigo) errors.codigo = "Informe o código.";
  else if (codigo.length > LIMITES.codigo) errors.codigo = `Máximo de ${LIMITES.codigo} caracteres.`;
  if (!nome) errors.nome = "Informe o nome do tipo de veículo.";
  else if (nome.length > LIMITES.nome) errors.nome = `Máximo de ${LIMITES.nome} caracteres.`;
  if (form.descricao.trim().length > LIMITES.descricao) {
    errors.descricao = `Máximo de ${LIMITES.descricao} caracteres.`;
  }
  const eixos = String(form.qtdEixos).trim();
  if (eixos && !(/^\d+$/.test(eixos) && Number(eixos) >= 1 && Number(eixos) <= LIMITES.eixos)) {
    errors.qtdEixos = `Inteiro de 1 a ${LIMITES.eixos}.`;
  }
  const capacidade = parseCapacidade(form.capacidade);
  if (capacidade !== null && (Number.isNaN(capacidade) || capacidade <= 0 || capacidade > LIMITES.capacidade)) {
    errors.capacidade = "Informe um peso em kg maior que zero.";
  }
  return errors;
}

export function payloadDoForm(form) {
  const eixos = String(form.qtdEixos).trim();
  return {
    codigo: form.codigo.trim(),
    nome: form.nome.trim(),
    descricao: form.descricao.trim(),
    qtdEixos: eixos ? Number(eixos) : null,
    capacidade: parseCapacidade(form.capacidade),
    status: form.status === "active",
    idEmbarcador: form.idEmbarcador ? Number(form.idEmbarcador) : null,
  };
}

// Frase da confirmação de inativar, a partir de GET /:id/uso.
export function resumoUso(uso) {
  if (!uso) return [];
  const partes = [];
  const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;
  if (uso.tabelasFrete) partes.push(plural(uso.tabelasFrete, "tabela de frete", "tabelas de frete"));
  if (uso.prazoTabelas) partes.push(plural(uso.prazoTabelas, "tabela de prazo", "tabelas de prazo"));
  if (uso.rotas) partes.push(plural(uso.rotas, "rota", "rotas"));
  if (uso.romaneios) partes.push(plural(uso.romaneios, "romaneio", "romaneios"));
  if (uso.veiculosFrota) partes.push(plural(uso.veiculosFrota, "veículo de frota própria", "veículos de frota própria"));
  return partes;
}

function mensagemErro(error, padrao) {
  return (error && error.response && error.response.data && error.response.data.error) || padrao;
}

function sortValue(row, field) {
  if (field === "status") return isActive(row.status) ? 1 : 0;
  if (field === "cliente") return row.idEmbarcador ? rotuloCliente(row) : "";
  if (field === "qtdEixos" || field === "capacidade") {
    return row[field] === null || row[field] === undefined ? -1 : Number(row[field]);
  }
  return row[field];
}

const stateBoxStyle = {
  textAlign: "center",
  padding: "2.5rem 1rem",
  color: AUTOPILOT_COLORS.muted,
};

const stateTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.98rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
};

const mutedCellStyle = { color: AUTOPILOT_COLORS.muted };

const MODAL_CLASS = "modal-align-top tipo-veiculo-modal";

const TiposVeiculo = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // A tela abre só com os ativos; os inativos aparecem pelo toggle da faixa de busca.
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [clienteFiltro, setClienteFiltro] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(createEmptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Confirmação de inativar: { item, uso, carregando, erro }.
  const [toInactivate, setToInactivate] = useState(null);
  const [changingStatus, setChangingStatus] = useState(false);

  const { user } = useAuth();
  const podeEditar = Boolean(user && user.setor === "admin");
  // Clientes do campo "Cliente": as empresas do login (o admin enxerga todas).
  const clientes = useMemo(
    () =>
      ((user && user.empresas) || [])
        .filter((e) => Number(e.value) > 0)
        .map((e) => ({ value: String(e.value), label: e.label }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), "pt-BR")),
    [user]
  );
  // Filtro: só os clientes que têm tipo exclusivo.
  const clientesComTipo = useMemo(() => {
    const vistos = {};
    (Array.isArray(data) ? data : []).forEach((item) => {
      if (item.idEmbarcador) vistos[item.idEmbarcador] = rotuloCliente(item);
    });
    return Object.keys(vistos)
      .map((id) => ({ value: id, label: vistos[id] }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [data]);
  const { addToast } = useToast();
  const debouncedSearch = useDebounce(searchTerm, 250);

  const toastErro = useCallback(
    (title, description) =>
      addToast({ place: "tr", type: "danger", title, description, icon: "tim-icons icon-alert-circle-exc" }),
    [addToast]
  );

  const toastOk = useCallback(
    (title, description) =>
      addToast({ place: "tr", type: "success", title, description, icon: "tim-icons icon-check-2" }),
    [addToast]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const result = await api.get(URL_BASE);
      setData(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      setData([]);
      setLoadError("Não foi possível buscar os tipos de veículo.");
      toastErro("Erro ao carregar", "Não foi possível buscar os tipos de veículo.");
    } finally {
      setLoading(false);
    }
  }, [toastErro]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const query = normalizeText(debouncedSearch);

    return (Array.isArray(data) ? data : []).filter((item) => {
      if (!mostrarInativos && !isActive(item.status)) return false;
      if (!passaFiltroCliente(item, clienteFiltro)) return false;
      if (query) {
        const index = normalizeText([item.codigo, item.nome].filter(Boolean).join(" "));
        if (!index.includes(query)) return false;
      }
      return true;
    });
  }, [data, debouncedSearch, mostrarInativos, clienteFiltro]);

  const sorted = useMemo(() => sortRows(filtered, sort, sortValue), [filtered, sort]);
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize]);

  // Mudou filtro, busca ou ordenação: volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, mostrarInativos, clienteFiltro, sort, pageSize]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(createEmptyForm());
    setFormErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((item) => {
    setEditing(item);
    setForm({
      codigo: item.codigo || "",
      nome: item.nome || "",
      descricao: item.descricao || "",
      qtdEixos: item.qtdEixos === null || item.qtdEixos === undefined ? "" : String(item.qtdEixos),
      capacidade: capacidadeParaForm(item.capacidade),
      status: isActive(item.status) ? "active" : "inactive",
      idEmbarcador: item.idEmbarcador ? String(item.idEmbarcador) : "",
    });
    setFormErrors({});
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    if (saving) return;
    setFormOpen(false);
  }, [saving]);

  // O valor é lido fora do updater (React 16: o evento sintético volta ao pool).
  const updateForm = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    const nextErrors = validarForm(form);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const payload = payloadDoForm(form);
    // Na edição o status muda pelo botão Ativar/Inativar (que mostra o uso antes).
    if (editing) delete payload.status;

    try {
      setSaving(true);
      if (editing) {
        await api.put(`${URL_BASE}/${editing.id}`, payload);
      } else {
        await api.post(URL_BASE, payload);
      }
      toastOk(
        editing ? "Tipo de veículo atualizado" : "Tipo de veículo cadastrado",
        editing ? "As alterações foram salvas." : "O tipo de veículo foi cadastrado com sucesso."
      );
      setFormOpen(false);
      loadData();
    } catch (error) {
      toastErro("Erro ao salvar", mensagemErro(error, "Não foi possível salvar o tipo de veículo."));
    } finally {
      setSaving(false);
    }
  }, [editing, form, loadData, toastErro, toastOk]);

  const openInactivate = useCallback(async (item) => {
    setToInactivate({ item, uso: null, carregando: true, erro: null });
    try {
      const result = await api.get(`${URL_BASE}/${item.id}/uso`);
      setToInactivate((atual) => (atual && atual.item.id === item.id ? { item, uso: result.data, carregando: false, erro: null } : atual));
    } catch (error) {
      const erro = mensagemErro(error, "Não foi possível consultar onde o tipo é usado.");
      setToInactivate((atual) => (atual && atual.item.id === item.id ? { item, uso: null, carregando: false, erro } : atual));
    }
  }, []);

  const closeInactivate = useCallback(() => {
    if (changingStatus) return;
    setToInactivate(null);
  }, [changingStatus]);

  const changeStatus = useCallback(
    async (item, ativo) => {
      try {
        setChangingStatus(true);
        await api.patch(`${URL_BASE}/${item.id}/status`, { status: ativo });
        toastOk(
          ativo ? "Tipo de veículo ativado" : "Tipo de veículo inativado",
          ativo
            ? `"${item.nome}" voltou a aparecer na escolha de tipo das tabelas de frete.`
            : `"${item.nome}" não aparece mais na escolha de tipo de novas tabelas de frete.`
        );
        setToInactivate(null);
        loadData();
      } catch (error) {
        toastErro(
          ativo ? "Não foi possível ativar" : "Não foi possível inativar",
          mensagemErro(error, "Erro ao alterar o status do tipo de veículo.")
        );
      } finally {
        setChangingStatus(false);
      }
    },
    [loadData, toastErro, toastOk]
  );

  const exportar = useCallback(() => {
    const ok = exportCsv({
      filename: "tipos_veiculo",
      columns: [
        { label: "Código", getValue: (r) => r.codigo || "" },
        { label: "Nome", getValue: (r) => r.nome || "" },
        { label: "Cliente", getValue: (r) => rotuloCliente(r) },
        { label: "Descrição", getValue: (r) => r.descricao || "" },
        { label: "Qtd. de eixos", getValue: (r) => (r.qtdEixos === null || r.qtdEixos === undefined ? "" : r.qtdEixos) },
        { label: "Capacidade (kg)", getValue: (r) => (r.capacidade === null || r.capacidade === undefined ? "" : formatKg(r.capacidade)) },
        { label: "Status", getValue: (r) => (isActive(r.status) ? "Ativo" : "Inativo") },
      ],
      rows: sorted,
    });
    if (!ok) {
      addToast({ place: "tr", type: "warning", title: "Nada para exportar", description: "Nenhum tipo de veículo no recorte atual." });
    }
  }, [addToast, sorted]);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setMostrarInativos(false);
    setClienteFiltro("");
  }, []);

  const handleSort = useCallback((field) => setSort((prev) => toggleSort(prev, field)), []);

  const hasFilters = normalizeText(searchTerm) !== "" || mostrarInativos || clienteFiltro !== "";
  const inativosOcultos = mostrarInativos
    ? 0
    : (Array.isArray(data) ? data : []).filter((item) => !isActive(item.status)).length;

  const renderListState = () => {
    if (loading) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
          Carregando tipos de veículo…
        </div>
      );
    }
    if (loadError) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>Não foi possível carregar</div>
          <p style={{ marginBottom: "0.75rem" }}>{loadError}</p>
          <Button size="sm" color="info" outline onClick={loadData}>
            Tentar novamente
          </Button>
        </div>
      );
    }
    if (sorted.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>Nenhum tipo de veículo encontrado</div>
          <p style={{ marginBottom: 0 }}>
            {hasFilters
              ? "Ajuste os filtros ou limpe a busca."
              : inativosOcultos
                ? "Não há tipos ativos. Ligue \"Mostrar inativos\" para ver os demais."
                : "O catálogo ainda está vazio."}
          </p>
        </div>
      );
    }
    return null;
  };

  const listState = renderListState();
  const usoPartes = toInactivate ? resumoUso(toInactivate.uso) : [];

  return (
    <div className="content tipos-veiculo-page">
      {/* Criação/edição: título/close/inputs tematizados por .modal-align-top */}
      <Modal isOpen={isFormOpen} toggle={closeForm} modalClassName={MODAL_CLASS} fade={false} size="md">
        <ModalHeader toggle={closeForm} tag="h5">
          {editing ? "Editar tipo de veículo" : "Novo tipo de veículo"}
        </ModalHeader>

        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <div className="tipo-veiculo-modal__aviso" role="note">
            <FaExclamationTriangle className="tipo-veiculo-modal__aviso-icone" />
            {form.idEmbarcador ? (
              <span>
                <strong>Exclusivo de um cliente.</strong> Só aparece nas tabelas de frete, cotações e
                romaneios deste cliente.
              </span>
            ) : (
              <span>
                <strong>Cadastro base.</strong> A alteração vale para todos os embarcadores, em
                tabelas de frete, cotação e recálculo.
              </span>
            )}
          </div>
          <Row>
            <Col md="5">
              <FormGroup>
                <Label for="tipo-veiculo-codigo">Código</Label>
                <Input
                  id="tipo-veiculo-codigo"
                  value={form.codigo}
                  maxLength={LIMITES.codigo}
                  onChange={(e) => updateForm("codigo", e.target.value)}
                  invalid={Boolean(formErrors.codigo)}
                  placeholder="Ex.: Truck"
                />
                {formErrors.codigo ? <FormFeedback>{formErrors.codigo}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="7">
              <FormGroup>
                <Label for="tipo-veiculo-nome">Nome</Label>
                <Input
                  id="tipo-veiculo-nome"
                  value={form.nome}
                  maxLength={LIMITES.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  invalid={Boolean(formErrors.nome)}
                  placeholder="Ex.: Truck"
                />
                {formErrors.nome ? <FormFeedback>{formErrors.nome}</FormFeedback> : null}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="tipo-veiculo-cliente">Cliente</Label>
            <Input
              id="tipo-veiculo-cliente"
              type="select"
              value={form.idEmbarcador}
              onChange={(e) => updateForm("idEmbarcador", e.target.value)}
            >
              <option value="">Base (todos os clientes)</option>
              {form.idEmbarcador && !clientes.some((c) => c.value === form.idEmbarcador) ? (
                <option value={form.idEmbarcador}>{editing ? rotuloCliente(editing) : form.idEmbarcador}</option>
              ) : null}
              {clientes.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Input>
            <small className="tipo-veiculo-modal__dica">
              Exclusivo: para veículo ou modalidade que só um cliente usa (ex.: os "Spot" de uma tabela).
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="tipo-veiculo-descricao">Descrição</Label>
            <Input
              id="tipo-veiculo-descricao"
              type="textarea"
              rows="3"
              value={form.descricao}
              maxLength={LIMITES.descricao}
              onChange={(e) => updateForm("descricao", e.target.value)}
              invalid={Boolean(formErrors.descricao)}
              placeholder="Ex.: Caminhão pesado (carga até 14 t)"
            />
            {formErrors.descricao ? (
              <FormFeedback>{formErrors.descricao}</FormFeedback>
            ) : (
              <small className="tipo-veiculo-modal__dica tipo-veiculo-modal__dica--direita">
                {form.descricao.length}/{LIMITES.descricao}
              </small>
            )}
          </FormGroup>

          <Row>
            <Col md={editing ? "6" : "4"}>
              <FormGroup>
                <Label for="tipo-veiculo-eixos">Qtd. de eixos</Label>
                <Input
                  id="tipo-veiculo-eixos"
                  inputMode="numeric"
                  value={form.qtdEixos}
                  maxLength={2}
                  onChange={(e) => updateForm("qtdEixos", e.target.value)}
                  invalid={Boolean(formErrors.qtdEixos)}
                  placeholder="Ex.: 3"
                />
                {formErrors.qtdEixos ? (
                  <FormFeedback>{formErrors.qtdEixos}</FormFeedback>
                ) : (
                  <small className="tipo-veiculo-modal__dica">De 1 a {LIMITES.eixos}. Opcional.</small>
                )}
              </FormGroup>
            </Col>
            <Col md={editing ? "6" : "4"}>
              <FormGroup>
                <Label for="tipo-veiculo-capacidade">Capacidade (kg)</Label>
                <Input
                  id="tipo-veiculo-capacidade"
                  inputMode="decimal"
                  value={form.capacidade}
                  onChange={(e) => updateForm("capacidade", e.target.value)}
                  invalid={Boolean(formErrors.capacidade)}
                  placeholder="Ex.: 14000"
                />
                {formErrors.capacidade ? (
                  <FormFeedback>{formErrors.capacidade}</FormFeedback>
                ) : (
                  <small className="tipo-veiculo-modal__dica">Peso máximo de carga. Opcional.</small>
                )}
              </FormGroup>
            </Col>
            {editing ? null : (
              <Col md="4">
                <FormGroup>
                  <Label for="tipo-veiculo-status">Status</Label>
                  <Input
                    id="tipo-veiculo-status"
                    type="select"
                    value={form.status}
                    onChange={(e) => updateForm("status", e.target.value)}
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </Input>
                </FormGroup>
              </Col>
            )}
          </Row>

          <div className="tipo-veiculo-modal__rodape">
            <Button color="default" outline size="sm" onClick={closeForm} disabled={saving}>
              Cancelar
            </Button>
            <Button color="info" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Confirmação de inativar, com o uso do tipo */}
      <Modal isOpen={Boolean(toInactivate)} toggle={closeInactivate} modalClassName={MODAL_CLASS} fade={false} size="md">
        <ModalHeader toggle={closeInactivate} tag="h5">
          Inativar tipo de veículo
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          {toInactivate && toInactivate.carregando ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
              Consultando onde o tipo é usado…
            </div>
          ) : (
            <>
              <p style={{ marginBottom: "0.75rem" }}>
                Confirma a inativação de <strong>{toInactivate ? toInactivate.item.nome : ""}</strong>?
              </p>
              {toInactivate && toInactivate.erro ? (
                <p className="tipo-veiculo-uso" style={mutedCellStyle}>
                  {toInactivate.erro}
                </p>
              ) : usoPartes.length > 0 ? (
                <p className="tipo-veiculo-uso">
                  Em uso em {usoPartes.join(", ")}.
                </p>
              ) : (
                <p className="tipo-veiculo-uso" style={mutedCellStyle}>
                  Este tipo não está em uso em nenhuma tabela de frete, prazo, rota ou romaneio.
                </p>
              )}
              <p style={{ ...mutedCellStyle, marginBottom: 0 }}>
                Ele deixa de aparecer na escolha de tipo de novas tabelas de frete, para todos os
                embarcadores. As tabelas que já usam este tipo continuam funcionando e você pode
                reativá-lo quando quiser.
              </p>
            </>
          )}
          <div className="tipo-veiculo-modal__rodape">
            <Button color="default" outline size="sm" onClick={closeInactivate} disabled={changingStatus}>
              Cancelar
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => changeStatus(toInactivate.item, false)}
              disabled={changingStatus || !toInactivate || toInactivate.carregando}
            >
              {changingStatus ? "Inativando..." : "Inativar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <PageHero
        eyebrow="Catálogos"
        title="Tipos de Veículo"
        description={
          <>
            Catálogo de tipos de veículo usado nas tabelas de frete, na cotação e no recálculo. O
            cadastro <strong>base</strong> vale para todos os embarcadores; um tipo exclusivo só
            aparece para o cliente dele.
            {podeEditar ? null : " A manutenção é feita pela Everlog."}
          </>
        }
        actions={
          <>
            <PageHeroRefreshButton onClick={loadData} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={loading || !sorted.length} />
            {podeEditar ? (
              <Button color="info" className="btn-sm" onClick={openCreate}>
                <FaPlus style={{ marginRight: 6 }} />
                Novo tipo de veículo
              </Button>
            ) : null}
          </>
        }
      />

      <FilterBand
        mode="client"
        searchLabel="Buscar tipo de veículo"
        searchHint="Código ou nome"
        searchPlaceholder="Buscar por código ou nome"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        extra={
          <>
            <Input
              id="tipo-veiculo-filtro-cliente"
              type="select"
              bsSize="sm"
              aria-label="Cliente"
              value={clienteFiltro}
              onChange={(e) => setClienteFiltro(e.target.value)}
              className="mr-2"
              style={{ width: "auto", minWidth: 170 }}
            >
              <option value="">Todos os clientes</option>
              <option value="base">Só o cadastro base</option>
              {clientesComTipo.map((c) => (
                <option key={c.value} value={c.value}>
                  Exclusivos: {c.label}
                </option>
              ))}
            </Input>
            <FormGroup check className="mb-0 mr-2" style={{ whiteSpace: "nowrap" }}>
              <Label check>
                <Input
                  id="tipo-veiculo-mostrar-inativos"
                  type="checkbox"
                  checked={mostrarInativos}
                  onChange={(e) => {
                    const marcado = e.target.checked;
                    setMostrarInativos(marcado);
                  }}
                />
                <span className="form-check-sign" />
                Mostrar inativos
              </Label>
            </FormGroup>
            {hasFilters ? (
              <Button size="sm" color="default" outline onClick={clearFilters}>
                Limpar
              </Button>
            ) : null}
          </>
        }
      />

      <ShellCard
        className="tipos-veiculo-lista"
        title="Tipos de veículo cadastrados"
        description={
          inativosOcultos
            ? `${sorted.length} resultado(s) · ${inativosOcultos} inativo(s) oculto(s)`
            : `${sorted.length} resultado(s)`
        }
      >
        {listState ? (
          listState
        ) : (
          <TableSurface>
            <Table size="sm" responsive hover className="tipos-veiculo-table hub-thead-accent">
              <thead className="text-primary">
                <tr>
                  <th style={{ width: "150px" }}>
                    <SortHeader label="Código" field="codigo" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Nome" field="nome" sort={sort} onSort={handleSort} />
                  </th>
                  <th style={{ width: "170px" }}>
                    <SortHeader label="Cliente" field="cliente" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Descrição" field="descricao" sort={sort} onSort={handleSort} />
                  </th>
                  <th style={{ width: "90px" }}>
                    <SortHeader label="Eixos" field="qtdEixos" sort={sort} onSort={handleSort} />
                  </th>
                  <th style={{ width: "140px" }}>
                    <SortHeader label="Capacidade (kg)" field="capacidade" sort={sort} onSort={handleSort} />
                  </th>
                  <th style={{ width: "110px" }}>
                    <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                  </th>
                  {podeEditar ? (
                    <th className="text-right" style={{ width: "110px" }}>
                      Ações
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {paged.items.map((item) => {
                  const ativo = isActive(item.status);
                  return (
                    <tr key={item.id}>
                      <td>{item.codigo || "—"}</td>
                      <td>{item.nome}</td>
                      <td style={item.idEmbarcador ? undefined : mutedCellStyle}>{rotuloCliente(item)}</td>
                      <td style={item.descricao ? undefined : mutedCellStyle}>{item.descricao || "—"}</td>
                      <td style={item.qtdEixos ? undefined : mutedCellStyle}>{item.qtdEixos || "—"}</td>
                      <td style={item.capacidade ? undefined : mutedCellStyle}>{formatKg(item.capacidade)}</td>
                      <td>
                        <Status status={ativo ? "Ativo" : "Inativo"} title={ativo ? "Ativo" : "Inativo"} />
                      </td>
                      {podeEditar ? (
                        <td className="text-right">
                          <Button color="link" size="sm" className="btn-icon" title="Editar" onClick={() => openEdit(item)}>
                            <FaPen />
                          </Button>
                          {ativo ? (
                            <Button
                              color="link"
                              size="sm"
                              className="btn-icon text-danger"
                              title="Inativar"
                              onClick={() => openInactivate(item)}
                            >
                              <FaBan />
                            </Button>
                          ) : (
                            <Button
                              color="link"
                              size="sm"
                              className="btn-icon text-success"
                              title="Ativar"
                              onClick={() => changeStatus(item, true)}
                              disabled={changingStatus}
                            >
                              <FaCheck />
                            </Button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              total={paged.total}
              itemLabel="tipo(s) de veículo"
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </TableSurface>
        )}
      </ShellCard>
    </div>
  );
};

export default TiposVeiculo;
